import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { useIsReadOnly } from '../../../hooks/useIsReadOnly';
import { useTranslation } from 'react-i18next';
import logger, { nativeConsole } from '../../../utils/logger';
import { mapErrorToUserMessage } from '../../../utils/userErrorMessage';
import { getPeriodError } from '../../../utils/billingPeriodValidation';
import { hasSellerTaxIdentification } from '../../../utils/sellerProfile';

// Mobile Components
import MobileCollapsibleSection from '../../ui/MobileCollapsibleSection';
import MobileInvoiceItemsForm from './MobileInvoiceItemsForm';
import { LanguageSelector } from '../../LanguageSelector/LanguageSelector';

// Existing Components (reused)
import InvoiceMetaForm from '../InvoiceMetaForm';
import BuyerDetailsForm from '../BuyerDetailsForm';
import SellerDetailsForm from '../SellerDetailsForm';
import ShipToAddressForm from '../ShipToAddressForm';
import OptionalFieldsForm from '../OptionalFieldsForm';
import RelatedOfferSelector from '../RelatedOfferSelector';
import { ConfirmationDialog } from '../../ConfirmationDialog';
import InvoiceTypeSwitch from '../../ui/InvoiceTypeSwitch';
import { InvoiceNumberPreview } from '../InvoiceNumberPreview';
import InvoiceDraftSelector from '../InvoiceDraftSelector';
import { INVOICE_FORM_ROUTE } from '../../../config/cockpitAccess';

// Types
import { Seller, Buyer, ShipToAddress, InvoiceItem, InvoiceData } from '../../../types/invoiceTypes';
import { DownPayment } from '../../../types/downPaymentTypes';

// Down Payment Components
import DownPaymentModal from '../../modals/DownPaymentModal';

// Hooks
import { useInvoiceForm } from '../../../hooks/invoice/useInvoiceForm';
import { useInvoiceSubmission } from '../../../hooks/invoice/useInvoiceSubmission';
import { useCustomerManagement } from '../../../hooks/invoice/useCustomerManagement';
import { useProductManagement } from '../../../hooks/invoice/useProductManagement';
import { useInvoiceNumberPreview } from '../../../hooks/invoice/useInvoiceNumberPreview';
import { useDeviceDetection } from '../../../hooks/useDeviceDetection';

// Firebase
import { auth } from '../../../firebaseConfig';
import { signOut } from 'firebase/auth';

// Draft API Functions
import { 
    saveInvoiceDraft, 
    generateZugferdPreview, 
    listInvoiceDrafts, 
    getInvoiceDraftDetails, 
    deleteInvoiceDraft 
} from '../../../api/invoiceDraftApi';
import { getAuth } from 'firebase/auth';
import { SingleValue } from 'react-select';
import { getTaxCategoryEntry, extractParagraphNr, requiresZeroRateCategoryChoice } from '../../../utils/taxCategoryCatalog';
import { InvoiceUsageMeter } from '../../usage/InvoiceUsageMeter';
import { useUsageLimitValueFraming } from '../../../hooks/invoice/useUsageLimitValueFraming';
import { FREEMIUM_INVOICE_LIMIT } from '../../../config/freemium';
import { buildUpgradeHref } from '../../../config/nudgePromotions';
import analyticsService from '../../../services/analyticsService';
import { formatCurrency } from '../../../utils/formatters';
import { centsToEuros } from '../../../utils/moneyUtils';

// VB-1 Feature-Flag (build-time, fail-closed, Default AUS).
const TAX_CATEGORY_ENABLED = process.env.REACT_APP_TAX_CATEGORY_ENABLED === 'true';

interface MobileInvoiceDashboardProps {
    invoiceType?: 'zugferd' | 'xrechnung';
}

const MobileInvoiceDashboard: React.FC<MobileInvoiceDashboardProps> = ({ invoiceType = 'zugferd' }) => {
    const { t } = useTranslation(['invoice', 'common']);
    const [hasRequiredSellerData, setHasRequiredSellerData] = useState<boolean>(true);
    const navigate = useNavigate();
    const { isMobile } = useDeviceDetection();

    // Draft Management State
    const [availableDrafts, setAvailableDrafts] = useState<any[]>([]);
    const [selectedDraftId, setSelectedDraftId] = useState<string | null>(null);
    const [currentDraftId, setCurrentDraftId] = useState<string | null>(null);
    const [isLoadingDrafts, setIsLoadingDrafts] = useState(false);
    const [isLoadingDraftDetails, setIsLoadingDraftDetails] = useState(false);
    const [draftListError, setDraftListError] = useState<string | null>(null);
    const [isSavingDraft, setIsSavingDraft] = useState(false);
    const [isGeneratingPreview, setIsGeneratingPreview] = useState(false);
    const [token, setToken] = useState<string | null>(null);

    // Down Payment Modal State
    const [showDownPaymentModal, setShowDownPaymentModal] = useState<boolean>(false);

    // Auth Context
    const { currentUser, sellerData, sellerDataLoading, saveSellerData, refreshUserData, isPremium } = useAuth();
    // M11b-3: advisor (read-only) darf keine Rechnung erstellen/speichern.
    const isReadOnly = useIsReadOnly();
    
    // Custom Hooks (same as desktop)
    const { 
        customerMeta, 
        checkForUpdate: checkCustomerUpdate, 
        handleCustomerSelection,
        dialogOpen: customerDialogOpen,
        setDialogOpen: setCustomerDialogOpen,
        dialogConfig: customerDialogConfig,
        handleDialogAction: handleCustomerDialogAction,
        releaseCustomerMetaLock,
        getCurrentCustomerMeta
    } = useCustomerManagement(); 

    const {
        productMetaForItems,
        checkAndHandleProductUpdate,
        handleProductSelection: handleProductSelectionForHook,
        resetProductManagement,
        dialogOpen: productDialogOpen,
        setDialogOpen: setProductDialogOpen,
        dialogConfig: productDialogConfig,
        handleDialogAction: handleProductDialogAction
    } = useProductManagement();

    const {
        // Form state values
        invoiceDate, currency, buyer, items, deliveryDate,
        useDifferentShipTo, shipToAddress, paymentTerms, notes, billingStartDate, 
        billingEndDate, tenderOrLotReference, invoicedObjectId, invoicedObjectScheme, 
        contractReference, projectReferenceId, projectReferenceName, paymentDueDate, 
        paymentMandateReference, buyerOrderReference, sellerOrderReference, selectedOfferId, 
        selectedOfferNumber, relatedOfferData, offerWorkflowReferenceId,
        // Down payment state values
        existingDownPayments, hasDownPayments, totalDownPaymentAmount, remainingAmount, 
        isLoadingDownPayments, showDownPaymentSection,
        // Inline down payment creation state values
        inlineDownPaymentType, inlineDownPaymentValue, inlineDownPaymentDate,
        inlineDownPaymentNotes, isCreatingInlineDownPayment,
        // VB-1: Steuerart-Default
        defaultTaxCategorySelection,
        // Form state setters
        setIsAdvancedMode, setInvoiceDate, setCurrency, setBuyer, setItems, setDeliveryDate,
        setUseDifferentShipTo, setShipToAddress, setPaymentTerms, setNotes, setBillingStartDate,
        setBillingEndDate, setTenderOrLotReference, setInvoicedObjectId, setInvoicedObjectScheme,
        setContractReference, setProjectReferenceId, setProjectReferenceName, setPaymentDueDate,
        setPaymentMandateReference, setBuyerOrderReference, setSellerOrderReference, setSelectedOfferId,
        setSelectedOfferNumber, setRelatedOfferData, setOfferWorkflowReferenceId,
        // Down payment setters
        setExistingDownPayments, setHasDownPayments, setTotalDownPaymentAmount, setRemainingAmount,
        setIsLoadingDownPayments, setShowDownPaymentSection,
        // Inline down payment creation setters
        setInlineDownPaymentType, setInlineDownPaymentValue, setInlineDownPaymentDate,
        setInlineDownPaymentNotes, setIsCreatingInlineDownPayment,
        resetForm: resetInvoiceForm,
        handleOfferSelection,
        handleInlineDownPaymentCreation,
        offerSelectionError
    } = useInvoiceForm();

    // Re-Loop-Auflage 🟡-2 (review-report-leistungszeitraum-mobil.md): derselbe
    // Fehlerzustand wie in OptionalFieldsForm, hier gebraucht, um ihn zusätzlich
    // am (auch zugeklappten) Abschnittskopf sichtbar zu machen.
    const billingPeriodError = useMemo(
        () => getPeriodError(billingStartDate, billingEndDate),
        [billingStartDate, billingEndDate]
    );

    const {
        submitInvoice,
        isLoading: isSubmitting,
        error: submissionError,
        errorType: submissionErrorType,
        setError: setSubmissionError
    } = useInvoiceSubmission(invoiceType);

    // Rechnungsnummer-Vorschau Hook
    const {
        zugferdPreview,
        isLoading: isLoadingPreview
    } = useInvoiceNumberPreview();

    // Effects (same as desktop - reused)
    useEffect(() => {
        if (!sellerDataLoading) {
            const hasData = !!(sellerData && sellerData.name && sellerData.street && sellerData.zip && sellerData.city && sellerData.country);
            // § 14 Abs. 4 Nr. 2 UStG: Steuernummer oder USt-IdNr. für ALLE Verkäufer (#339)
            setHasRequiredSellerData(hasData && hasSellerTaxIdentification(sellerData));
        }
    }, [sellerData, sellerDataLoading]);

    useEffect(() => {
        const hasShipToData = !!(shipToAddress.name || shipToAddress.street || shipToAddress.street2 || shipToAddress.street3 || (shipToAddress.zip && shipToAddress.zip !== '') || (shipToAddress.city && shipToAddress.city !== '') || (shipToAddress.country && shipToAddress.country !== 'DE'));
        if (hasShipToData !== useDifferentShipTo) {
            setUseDifferentShipTo(hasShipToData);
        }
    }, [shipToAddress, useDifferentShipTo, setUseDifferentShipTo]);

    // Token Management (same as desktop)
    useEffect(() => {
        const getToken = async () => {
            if (currentUser) {
                try {
                    const authToken = await getAuth().currentUser?.getIdToken(true);
                    setToken(authToken || null);
                } catch (error) {
                    console.error("Fehler beim Laden des Auth-Tokens:", error);
                    setToken(null);
                }
            } else {
                setToken(null);
            }
        };
        getToken();
    }, [currentUser]);

    // Load Drafts (same as desktop)
    useEffect(() => {
        const loadDrafts = async () => {
            if (!token) return;
            
            setIsLoadingDrafts(true);
            setDraftListError(null);
            
            try {
                const drafts = await listInvoiceDrafts(token);
                const filteredDrafts = drafts.filter(draft => draft.type === invoiceType);
                setAvailableDrafts(filteredDrafts);
                console.log(`${invoiceType}-Draft-Liste geladen:`, filteredDrafts);
            } catch (error: any) {
                console.error('Fehler beim Laden der ZUGFeRD-Drafts:', error);
                setDraftListError(mapErrorToUserMessage(error, t, { fallbackKey: 'errors.draft.loadFailed' }));
            } finally {
                setIsLoadingDrafts(false);
            }
        };

        loadDrafts();
    }, [token, t]);

    // Event-Listener für Draft-Löschung (same as desktop)
    useEffect(() => {
        const handleDraftDeleted = (event: CustomEvent) => {
            const { deletedDraftId } = event.detail;
            
            setAvailableDrafts(prevDrafts => 
                prevDrafts.filter(draft => draft.id !== deletedDraftId)
            );
            
            if (selectedDraftId === deletedDraftId) {
                setSelectedDraftId(null);
            }
            
            logger.info(`Draft-Liste aktualisiert nach Löschung von Draft: ${deletedDraftId}`);
        };

        window.addEventListener('draftDeleted', handleDraftDeleted as EventListener);

        return () => {
            window.removeEventListener('draftDeleted', handleDraftDeleted as EventListener);
        };
    }, [selectedDraftId]);

    // Callback Handlers (same as desktop - reused)
    const handleLogout = useCallback(async () => {
        try {
            await signOut(auth);
            navigate('/login');
        } catch (error) {
            logger.error(error, "logout error");
        }
    }, [navigate]);

    const resetAllForms = useCallback(() => {
        resetInvoiceForm();
        resetProductManagement();
        nativeConsole.log("All relevant form states reset.");
    }, [resetInvoiceForm, resetProductManagement]);

    const closeAllDialogs = useCallback(() => {
        setCustomerDialogOpen(false);
        setProductDialogOpen(false);
    }, [setCustomerDialogOpen, setProductDialogOpen]);

    // Form submission handlers (same as desktop - reused for brevity)
    const validateAndHandleUpdates = useCallback(async (): Promise<boolean> => {
        try {
            nativeConsole.log('=== validateAndHandleUpdates START ===');
            nativeConsole.log('buyer:', buyer);
            nativeConsole.log('items:', items);
            
            const customerResult = await checkCustomerUpdate(buyer);
            nativeConsole.log('checkCustomerUpdate result:', customerResult);
            
            if (customerResult === false) {
                nativeConsole.log('Customer validation failed - stopping process');
                return false;
            }

            const productResult = await checkAndHandleProductUpdate(items);
            nativeConsole.log('checkAndHandleProductUpdate result:', productResult);
            
            if (productResult === false) {
                nativeConsole.log('Product validation failed - stopping process');
                return false;
            }

            nativeConsole.log('=== validateAndHandleUpdates SUCCESS ===');
            return true;
        } catch (error) {
            nativeConsole.error('Fehler bei der Validierung:', error);
            logger.error('Fehler bei der Validierung:', error);
            return false;
        }
    }, [buyer, items, checkCustomerUpdate, checkAndHandleProductUpdate]);

    // Handle form submit (same logic as desktop)
    const handleFormSubmit = useCallback(async (e: React.FormEvent) => {
        e.preventDefault();

        try {
            // VB-1: Fail-closed-Validierung der Steuerart (jurist A1/A2/VETO-4). Nie still durchlassen.
            if (TAX_CATEGORY_ENABLED) {
                // 0 %-Satz ist mehrdeutig: jede 0 %-Position MUSS eine eindeutige
                // Steuerart tragen, sonst entstünde eine widersprüchliche Rechnung
                // (0 % + Kategorie „Standard"). Submit hart blockieren und auf die
                // betroffenen Positionen zeigen.
                const unresolvedZeroRatePositions = items
                    .map((item, index) => ({ item, index }))
                    .filter(({ item }) => requiresZeroRateCategoryChoice(item.taxRate, item.taxCategorySelection))
                    .map(({ index }) => index + 1);
                if (unresolvedZeroRatePositions.length > 0) {
                    setSubmissionError(
                        t('taxCategory.validation.zeroRateCategoryRequired', {
                            positions: unresolvedZeroRatePositions.join(', '),
                        })
                    );
                    return;
                }
                for (const item of items) {
                    const entry = getTaxCategoryEntry(item.taxCategorySelection);
                    if (!entry) continue;
                    if (entry.requiresBuyerVatId && !(buyer.vatNumber ?? '').trim()) {
                        setSubmissionError(t('taxCategory.validation.buyerVatIdRequired'));
                        return;
                    }
                    if (entry.requiresParagraphNr && !extractParagraphNr(item.taxCategorySelection, item.exemptionReason).trim()) {
                        setSubmissionError(t('taxCategory.validation.paragraphNrRequired'));
                        return;
                    }
                    // VETO-4: Kategorie K (§ 6a IGL) erfordert Leistungs-/Lieferdatum (BT-72, EN-16931).
                    if (entry.bt151Code === 'K' && !(deliveryDate ?? '').trim()) {
                        setSubmissionError(t('taxCategory.validation.kDeliveryDateRequired'));
                        return;
                    }
                }
            }

            const validationSuccessful = await validateAndHandleUpdates();
            if (!validationSuccessful) {
                return;
            }
            
            const currentCustomerMeta = getCurrentCustomerMeta();
            await submitInvoice(
                {
                    invoiceDate, currency, buyer, 
                    deliveryDate, useDifferentShipTo, shipToAddress: useDifferentShipTo ? shipToAddress : null, 
                    paymentTerms, notes, billingStartDate, billingEndDate, tenderOrLotReference,
                    invoicedObjectId, invoicedObjectScheme, contractReference, projectReferenceId,
                    projectReferenceName, paymentDueDate, paymentMandateReference, buyerOrderReference,
                    sellerOrderReference, selectedOfferId, offerWorkflowReferenceId,
                    
                    customerMeta: currentCustomerMeta, 
                    
                    items: items.map(item => ({
                        ...item,
                        itemAction: productMetaForItems[item.id]?.action || 'none',
                        productId: productMetaForItems[item.id]?.productId || null,
                    })),
                    productAction: Object.values(productMetaForItems).some(meta => meta.action === 'create' || meta.action === 'update') 
                                ? 'update' 
                                : 'none',
                    selectedDraftId,
                },
                resetAllForms
            );
            releaseCustomerMetaLock();
        } catch (error) {
            nativeConsole.error('Fehler bei der Rechnungserstellung:', error);
            setSubmissionError(mapErrorToUserMessage(error, t, { fallbackKey: 'errors.invoice.saveFailed' }));
        }
    }, [
        buyer, items, validateAndHandleUpdates, submitInvoice, invoiceDate, currency, deliveryDate,
        useDifferentShipTo, shipToAddress, paymentTerms, notes, billingStartDate, billingEndDate,
        tenderOrLotReference, invoicedObjectId, invoicedObjectScheme, contractReference,
        projectReferenceId, projectReferenceName, paymentDueDate, paymentMandateReference,
        buyerOrderReference, sellerOrderReference, selectedOfferId, offerWorkflowReferenceId,
        customerMeta, productMetaForItems, resetAllForms, setSubmissionError, selectedDraftId, t,
    ]);

    // Draft Management Handlers (same as desktop - simplified for brevity)
    const handleSaveDraft = async () => {
        if (!token) return;
        setIsSavingDraft(true);

        try {
            const invoiceData = {
                invoiceDate, currency, buyer, items, deliveryDate, 
                useDifferentShipTo, 
                shipToAddress: useDifferentShipTo ? shipToAddress : null, 
                paymentTerms, notes, billingStartDate, billingEndDate, 
                tenderOrLotReference, invoicedObjectId, invoicedObjectScheme, 
                contractReference, projectReferenceId, projectReferenceName, 
                paymentDueDate, paymentMandateReference, buyerOrderReference,
                sellerOrderReference,
                seller: sellerData!,
                type: invoiceType,
                relatedOfferIds: selectedOfferId ? [selectedOfferId] : undefined,
                workflowReferenceId: offerWorkflowReferenceId || undefined
            };

            const { draftId } = await saveInvoiceDraft(invoiceData, token);
            console.log('ZUGFeRD-Draft erfolgreich gespeichert:', draftId);

            const drafts = await listInvoiceDrafts(token);
            const zugferdDrafts = drafts.filter(draft => draft.type === 'zugferd');
            setAvailableDrafts(zugferdDrafts);
        } catch (error: any) {
            console.error('Fehler beim Speichern des ZUGFeRD-Drafts:', error);
            setDraftListError(mapErrorToUserMessage(error, t, { fallbackKey: 'errors.draft.saveFailed' }));
        } finally {
            setIsSavingDraft(false);
        }
    };

    // Generate Preview Handler (same as desktop)
    const handleGeneratePreview = async () => {
        if (!token) {
            console.error('Kein Auth-Token verfügbar für Preview-Generierung');
            return;
        }

        setIsGeneratingPreview(true);

        try {
            const invoiceData = {
                invoiceDate, currency, buyer, items, deliveryDate, 
                useDifferentShipTo, 
                shipToAddress: useDifferentShipTo ? shipToAddress : null, 
                paymentTerms, notes, billingStartDate, billingEndDate, 
                tenderOrLotReference, invoicedObjectId, invoicedObjectScheme, 
                contractReference, projectReferenceId, projectReferenceName, 
                paymentDueDate, paymentMandateReference, buyerOrderReference,
                sellerOrderReference,
                seller: sellerData!,
                type: invoiceType
            };

            let pdfBlob: Blob;
            let draftId: string;

            // Prüfen ob bereits ein Entwurf existiert (currentDraftId oder selectedDraftId)
            const existingDraftId = currentDraftId || selectedDraftId;
            
            if (existingDraftId) {
                // Bestehenden Entwurf löschen, bevor neue Vorschau generiert wird
                console.log('Lösche bestehenden Draft vor neuer Vorschau-Generierung:', existingDraftId);
                try {
                    await deleteInvoiceDraft(existingDraftId, token);
                    console.log('Alter Draft erfolgreich gelöscht, generiere jetzt neue Vorschau');
                } catch (deleteError) {
                    console.warn('Draft-Löschung fehlgeschlagen, fahre mit normaler Vorschau fort:', deleteError);
                }
            }
            
            // Neue Vorschau generieren (erstellt neuen Entwurf)
            console.log('Generiere neue ZUGFeRD-Preview');
            const result = await generateZugferdPreview(invoiceData, token);
            pdfBlob = result.pdfBlob;
            draftId = result.draftId;
            setCurrentDraftId(draftId); // Neue Draft-ID für zukünftige Updates merken
            setSelectedDraftId(draftId); // Auch als ausgewählten Entwurf setzen für UI-Anzeige
            console.log('ZUGFeRD-Preview erfolgreich generiert:', draftId);

            // PDF herunterladen
            const url = window.URL.createObjectURL(pdfBlob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `ZUGFeRD-Vorschau-${new Date().toISOString().split('T')[0]}.pdf`;
            link.setAttribute('type', 'application/pdf');
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            
            // Blob-URL nach Verwendung freigeben
            setTimeout(() => URL.revokeObjectURL(url), 5000);
            
            // Draft-Liste neu laden
            const drafts = await listInvoiceDrafts(token);
            const zugferdDrafts = drafts.filter(draft => draft.type === 'zugferd');
            setAvailableDrafts(zugferdDrafts);
            
            logger.info(`ZUGFeRD-Vorschau erfolgreich erstellt mit Draft-ID: ${draftId}.`);
            setSubmissionError(null);
        } catch (err: any) {
            console.error('Fehler beim Generieren der ZUGFeRD-Vorschau:', err);
            logger.error('Fehler beim Generieren der ZUGFeRD-Vorschau:', err);
            setSubmissionError(mapErrorToUserMessage(err, t, { fallbackKey: 'errors.invoice.previewFailed' }));
        } finally {
            setIsGeneratingPreview(false);
        }
    };

    // NEU: Funktion zum Öffnen und Scrollen zum Abschlagsbereich (für Desktop-Weiterleitung)
    const handleOpenDownPaymentSection = () => {
        // In der mobilen Ansicht gibt es keinen Abschlagsbereich, 
        // also zeigen wir eine Nachricht oder leiten zur Desktop-Ansicht weiter
        alert('Abschlagsrechnungen sind in der Desktop-Ansicht verfügbar. Bitte wechseln Sie zur Desktop-Ansicht für diese Funktion.');
    };

    // Combine all potential errors for display
    const showUsageLimitPromo = submissionErrorType === 'usageLimit';
    const displayError = showUsageLimitPromo ? offerSelectionError : (submissionError || offerSelectionError);

    // M-UX-G4: Value-Framing für den 403-Limit-Moment (fetch-on-demand, s. Hook-JSDoc).
    const { revenueCents: usageLimitRevenueCents, resolved: usageLimitRevenueResolved } = useUsageLimitValueFraming(
        currentUser?.uid,
        showUsageLimitPromo,
    );

    // Ref für Auto-Scroll zum Fehler
    const errorRef = useRef<HTMLDivElement>(null);

    // Auto-scroll zum Fehler wenn einer auftritt
    useEffect(() => {
        if (displayError && errorRef.current) {
            errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }, [displayError]);

    // M-N3 (architecture-nudge.md §7.4): view_promotion GENAU 1x pro Banner-
    // Erscheinen (steigende Flanke von showUsageLimitPromo), unabhängig davon,
    // wie oft der Effekt wegen der async Umsatz-Auflösung erneut läuft — dafür
    // ein Ref-Latch statt eines reinen Dep-Arrays. Wir warten bewusst auf
    // `usageLimitRevenueResolved`, bevor wir feuern, statt sofort mit einem
    // Zwischenstand zu feuern: sonst würde für umsatztragende Nutzer zuerst
    // 'generic' und nach Auflösung des Fetches erneut 'value_framed' feuern
    // (Doppel-Fire, F-1 Review-Finding) — creative_slot kodiert value_framed
    // (PromotionEventData/M-N1 hat kein eigenes Boolean-Feld dafür).
    const usageLimitBannerFiredRef = useRef(false);
    useEffect(() => {
        if (!showUsageLimitPromo) {
            usageLimitBannerFiredRef.current = false;
            return;
        }
        if (usageLimitBannerFiredRef.current || !usageLimitRevenueResolved) return;
        usageLimitBannerFiredRef.current = true;
        analyticsService.trackViewPromotion({
            promotion_id: 'usage_limit_banner',
            creative_slot: usageLimitRevenueCents !== null ? 'value_framed' : 'generic',
            limit_value: FREEMIUM_INVOICE_LIMIT,
        });
    }, [showUsageLimitPromo, usageLimitRevenueResolved, usageLimitRevenueCents]);

    // Render - Mobile-optimized layout
    return (
        <div className="min-h-screen bg-surface text-primary">
            {/* Mobile Header */}
            <div className="sticky top-0 z-10 bg-surface border-b border-hairline px-4 py-4">
                <div className="flex justify-between items-center mb-3">
                    <h1 className="text-xl font-bold text-accent-ink">{t('newInvoice')}</h1>
                    <LanguageSelector />
                </div>
                <InvoiceTypeSwitch currentType={invoiceType} basePath="/dashboard" zugferdPath={INVOICE_FORM_ROUTE} />
            </div>

            <div className="px-4 py-4 space-y-4">
                {/* Info-Box über Rechnungsformat - Mobile optimiert */}
                <div className="p-4 bg-[var(--tone-info-bg)] border border-blue-500 rounded-lg">
                    <div className="flex items-start">
                        <svg className="w-5 h-5 text-[var(--tone-info-fg)] mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                        <div>
                            <h3 className="text-sm font-medium text-[var(--tone-info-fg)]">
                                {invoiceType === 'xrechnung' ? 'XRechnung Format' : 'ZUGFeRD Format'}
                            </h3>
                            <p className="text-sm text-[var(--tone-info-fg)] mt-1">
                                {invoiceType === 'xrechnung' 
                                    ? 'Diese Seite erstellt Rechnungen im XRechnung-Format (reines XML), das für die elektronische Rechnungsstellung an öffentliche Auftraggeber in Deutschland vorgeschrieben ist.'
                                    : 'Diese Seite erstellt Rechnungen im ZUGFeRD-Format (PDF mit eingebetteter XML), das sowohl für Menschen als auch für die automatische Verarbeitung geeignet ist.'}
                            </p>
                        </div>
                    </div>
                </div>

                {/* M-UX-G4: Free-Tier-Verbrauchszähler — VOR dem Limit-Moment sichtbar, kein Zähler für Premium.
                    Gate auf !sellerDataLoading: verhindert einen "0 von 4"-Flash für Premium-Nutzer, solange
                    isPremium noch seinen Default (false) trägt, während sellerData lädt. */}
                {!sellerDataLoading && (
                    <InvoiceUsageMeter count={sellerData?.monthlyInvoiceCount ?? 0} isPremium={isPremium} />
                )}

                {/* Draft Management - Mobile optimiert */}
                {hasRequiredSellerData && (
                    <div className="mb-4 p-4 border border-hairline rounded-lg bg-surface-raised">
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="text-lg font-medium text-primary">{t('drafts', { ns: 'navigation' })}</h3>
                            {availableDrafts.length > 0 && (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                                    {availableDrafts.length}
                                </span>
                            )}
                        </div>
                        <InvoiceDraftSelector
                            drafts={availableDrafts}
                            selectedDraftId={selectedDraftId}
                            onSelectDraft={async (selectedOption: SingleValue<any>) => {
                                // Draft selection logic (same as desktop)
                                // Simplified for brevity - would include full logic
                            }}
                            onDeleteDraft={async (draftId: string) => {
                                // Draft deletion logic (same as desktop)
                                // Simplified for brevity
                            }}
                            isLoading={isLoadingDrafts}
                            error={draftListError}
                            filterType={invoiceType}
                        />
                    </div>
                )}

                {/* Seller Data Check */}
                {!sellerDataLoading && !hasRequiredSellerData && (
                    <div className="p-6 bg-[var(--tone-accent-bg)] border border-indigo-500 rounded-lg">
                        <div className="text-center">
                            <h3 className="text-lg font-semibold text-primary mb-3">{t('common:sellerDataIncomplete.title')}</h3>
                            <p className="text-secondary mb-4 text-sm">
                                {!hasSellerTaxIdentification(sellerData) 
                                    ? t('common:sellerDataIncomplete.taxIdMissing')
                                    : t('common:sellerDataIncomplete.message')}
                            </p>
                            <Link 
                                to="/dashboard/konto" 
                                className="inline-block px-5 py-3 text-[#0C1322] bg-indigo-600 hover:bg-indigo-700 rounded-md font-medium text-sm"
                            >
                                {t('common:sellerDataIncomplete.completeData')}
                            </Link>
                        </div>
                    </div>
                )}

                {showUsageLimitPromo && (
                    <div className="p-4 bg-[var(--tone-accent-bg)] border border-indigo-600 text-[var(--tone-accent-fg)] rounded-lg" role="status">
                        <div className="flex items-start">
                            <svg className="w-5 h-5 text-accent-ink mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M12 3a9 9 0 100 18 9 9 0 000-18z" />
                            </svg>
                            <div className="flex-1">
                                <h3 className="text-base font-semibold text-[var(--tone-accent-fg)]">
                                    {t('common:usageLimitPromo.title', { limit: FREEMIUM_INVOICE_LIMIT })}
                                </h3>
                                <p className="text-sm mt-1">
                                    {usageLimitRevenueCents !== null
                                        ? t('common:usageLimitPromo.valueMessage', {
                                              amount: formatCurrency(centsToEuros(usageLimitRevenueCents)),
                                          })
                                        : t('common:usageLimitPromo.message')}
                                </p>
                                <p className="text-xs mt-1">
                                    {t('common:usageLimitPromo.yearlyAnchor')}
                                </p>
                                <div className="mt-3 flex flex-wrap items-center gap-3">
                                    <Link
                                        to={buildUpgradeHref('usage_limit_banner')}
                                        onClick={() => analyticsService.trackSelectPromotion({
                                            promotion_id: 'usage_limit_banner',
                                            creative_slot: usageLimitRevenueCents !== null ? 'value_framed' : 'generic',
                                            limit_value: FREEMIUM_INVOICE_LIMIT,
                                        })}
                                        className="inline-flex items-center px-4 py-2 rounded-md bg-indigo-500 hover:bg-indigo-400 text-[#0C1322] text-sm font-semibold transition-colors"
                                    >
                                        {t('common:usageLimitPromo.cta')}
                                    </Link>
                                    <span className="text-xs text-[var(--tone-accent-fg)]">
                                        {t('common:usageLimitPromo.note')}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Error Display */}
                {displayError && (
                    <div 
                        ref={errorRef}
                        className="p-4 bg-[var(--tone-danger-bg)] border border-[var(--tone-danger-border)] text-[var(--tone-danger-fg)] rounded-lg"
                        role="alert"
                    >
                        <strong className="font-bold">{t('form.error')}:</strong>
                        <span className="block mt-1 text-sm">{displayError}</span>
                    </div>
                )}

                {/* Main Form - Mobile Collapsible Sections */}
                {hasRequiredSellerData && (
                    <form onSubmit={hasDownPayments ? handleFormSubmit : handleFormSubmit}>

                        {/* Angebot auswählen */}
                        <div className="mb-4 p-4 border border-hairline rounded-lg bg-surface-raised">
                            <RelatedOfferSelector 
                                onSelectOffer={handleOfferSelection} 
                                selectedOfferId={selectedOfferId} 
                            />
                            {selectedOfferId && (
                                <div className="mt-3 text-sm text-muted">
                                    {t('offer.invoiceBasedOn')}: <span className="font-medium text-primary">{selectedOfferNumber}</span>
                                </div>
                            )}
                        </div>

                        {/* Rechnungsdetails */}
                        <MobileCollapsibleSection 
                            title={t('sections.invoiceDetails')} 
                            defaultExpanded={true} 
                            required
                        >
                            <InvoiceMetaForm 
                                invoiceDate={invoiceDate}
                                setInvoiceDate={setInvoiceDate}
                                currency={currency}
                                setCurrency={setCurrency}
                                deliveryDate={deliveryDate}
                                setDeliveryDate={setDeliveryDate}
                                invoiceNumberPreview={zugferdPreview?.preview}
                                isLoadingPreview={isLoadingPreview}
                                showTitle={false}
                            />
                        </MobileCollapsibleSection>
                        
                        {/* Käuferinformationen */}
                        <MobileCollapsibleSection 
                            title={t('sections.buyerInformation')} 
                            defaultExpanded={true} 
                            required
                        >
                            <BuyerDetailsForm 
                                buyer={buyer}
                                setBuyer={setBuyer}
                                onCustomerSelect={handleCustomerSelection}
                                showTitle={false}
                            />
                        </MobileCollapsibleSection>
                        
                        {/* Rechnungspositionen - Mobile-optimiert */}
                        <MobileCollapsibleSection 
                            title={t('sections.invoicePositions')} 
                            defaultExpanded={true} 
                            required
                            badge={items.length}
                        >
                            <MobileInvoiceItemsForm
                                items={items}
                                setItems={setItems}
                                currency={currency}
                                showTitle={false}
                                defaultTaxCategorySelection={defaultTaxCategorySelection}
                                buyerVatId={buyer.vatNumber}
                            />
                        </MobileCollapsibleSection>

                        {/* Erweiterte Optionen */}
                        <MobileCollapsibleSection
                            title={t('sections.advancedOptions')}
                            defaultExpanded={false}
                            error={
                                billingPeriodError
                                    ? (billingPeriodError === 'incomplete'
                                        ? t('advanced.periodIncompleteError')
                                        : t('advanced.periodOrderError'))
                                    : undefined
                            }
                        >
                            <OptionalFieldsForm
                                paymentTerms={paymentTerms}
                                setPaymentTerms={setPaymentTerms}
                                notes={notes}
                                setNotes={setNotes}
                                billingStartDate={billingStartDate}
                                setBillingStartDate={setBillingStartDate}
                                billingEndDate={billingEndDate}
                                setBillingEndDate={setBillingEndDate}
                                tenderOrLotReference={tenderOrLotReference}
                                setTenderOrLotReference={setTenderOrLotReference}
                                invoicedObjectId={invoicedObjectId}
                                setInvoicedObjectId={setInvoicedObjectId}
                                invoicedObjectScheme={invoicedObjectScheme}
                                setInvoicedObjectScheme={setInvoicedObjectScheme}
                                contractReference={contractReference}
                                setContractReference={setContractReference}
                                projectReferenceId={projectReferenceId}
                                setProjectReferenceId={setProjectReferenceId}
                                projectReferenceName={projectReferenceName}
                                setProjectReferenceName={setProjectReferenceName}
                                paymentDueDate={paymentDueDate}
                                setPaymentDueDate={setPaymentDueDate}
                                paymentMandateReference={paymentMandateReference}
                                setPaymentMandateReference={setPaymentMandateReference}
                                buyerOrderReference={buyerOrderReference}
                                setBuyerOrderReference={setBuyerOrderReference}
                                sellerOrderReference={sellerOrderReference}
                                setSellerOrderReference={setSellerOrderReference}
                            />
                        </MobileCollapsibleSection>

                        {/* Action Buttons - Mobile optimiert — M11b-3: für advisor (read-only) ausgeblendet */}
                        {!isReadOnly && (
                        <div className="sticky bottom-0 bg-surface border-t border-hairline p-4 space-y-3 -mx-4">
                            {/* Draft Save Button */}
                            <button
                                type="button"
                                onClick={handleSaveDraft}
                                disabled={isSavingDraft || !token}
                                className="w-full py-3 px-4 border border-transparent shadow-sm text-base font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isSavingDraft ? (
                                    <span className="flex items-center justify-center">
                                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        {t('loading', { ns: 'common' })}...
                                    </span>
                                ) : (
                                    t('draft.saveAsDraft')
                                )}
                            </button>

                            {/* Preview Generate Button */}
                            <button
                                type="button"
                                onClick={handleGeneratePreview}
                                disabled={isGeneratingPreview || !token}
                                className="w-full py-3 px-4 border border-transparent shadow-sm text-base font-medium rounded-md text-white bg-yellow-600 hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 transition disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isGeneratingPreview ? (
                                    <span className="flex items-center justify-center">
                                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        {t('loading', { ns: 'common' })}...
                                    </span>
                                ) : (
                                    t('actions.generatePreview')
                                )}
                            </button>

                            {/* Abschlag Button - Nur anzeigen wenn offerWorkflowReferenceId vorhanden */}
                            {offerWorkflowReferenceId && (
                                <button
                                    type="button"
                                    onClick={handleOpenDownPaymentSection}
                                    className="w-full py-3 px-4 border border-transparent shadow-sm text-base font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition"
                                >
                                    <span className="flex items-center justify-center">
                                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                        </svg>
                                        Abschlag
                                    </span>
                                </button>
                            )}

                            {/* Submit Button */}
                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className="w-full py-3 px-4 border border-transparent shadow-sm text-base font-medium rounded-md text-[#0C1322] bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition"
                            >
                                {isSubmitting ? (
                                    <span className="flex items-center justify-center">
                                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        {t('loading', { ns: 'common' })}...
                                    </span>
                                ) : (
                                    hasDownPayments ? t('actions.createFinalInvoice') : t('actions.createInvoice')
                                )}
                            </button>
                        </div>
                        )}
                    </form>
                )}
            </div>

            {/* Customer Dialog (same as desktop) */}
            <ConfirmationDialog 
                isOpen={customerDialogOpen}
                isProcessing={isSubmitting}
                title={customerDialogConfig.title}
                message={customerDialogConfig.message}
                options={customerDialogConfig.options || []}
                onClose={() => setCustomerDialogOpen(false)}
                dataComparison={customerDialogConfig.action === 'update' ? {
                    original: customerDialogConfig.original || {},
                    modified: customerDialogConfig.data,
                    displayFields: [
                        { key: 'name', label: 'Name' },
                        { key: 'companyName', label: 'Firmenname' },
                        { key: 'personName', label: 'Ansprechpartner' },
                        { key: 'street', label: 'Straße' },
                        { key: 'zip', label: 'PLZ' },
                        { key: 'city', label: 'Ort' },
                        { key: 'country', label: 'Land' }
                    ]
                } : undefined}
            />

            {/* Product Dialog (same as desktop) */}
            <ConfirmationDialog 
                isOpen={productDialogOpen}
                isProcessing={isSubmitting}
                title={productDialogConfig.title}
                message={productDialogConfig.message}
                options={[
                    { 
                        label: 'Änderungen speichern', 
                        action: () => handleProductDialogAction('process'),
                        variant: 'primary' 
                    },
                    { 
                        label: 'Abbrechen', 
                        action: () => handleProductDialogAction('cancel'),
                        variant: 'secondary'
                    }
                ]}
                onClose={() => setProductDialogOpen(false)}
                dataComparison={productDialogConfig.itemsToProcess.length === 1 && productDialogConfig.itemsToProcess[0].action === 'update' ? {
                    original: productDialogConfig.itemsToProcess[0].originalProduct || {},
                    modified: productDialogConfig.itemsToProcess[0].item,
                    displayFields: [
                        { key: 'name', label: 'Name' },
                        { key: 'description', label: 'Beschreibung' },
                        { key: 'netPrice', label: 'Netto-Preis' },
                        { key: 'taxRate', label: 'Steuersatz' },
                        { key: 'unitCode', label: 'Einheit' }
                    ]
                } : undefined}
            />
        </div>
    );
};

export default MobileInvoiceDashboard;
